<template>
  <div class="form-review">
    <div class="form-review__topbar">
      <button class="form-review__back" @click="goBack">
        <BaseIcon class="form-review__back-icon" name="arrow-left" />
        <span class="form-review__back-text">{{ $t('page.opportunity-forms.form-review.back-to-forms') }}</span>
      </button>
      <div class="form-review__name">
        <BaseIcon name="file-text" class="form-review__name-icon" />
        <span class="form-review__name-text">{{ form ? form.name : '' }}</span>
      </div>

      <PDFZoom v-if="form && pdf" class="form-review__zoom" />

      <div class="form-review__downloads" v-if="form">
        <BaseRoundButton
          v-if="isPhone"
          icon="download"
          :size="44"
          is-transparent
          :aria-label="$t('page.opportunity-forms.download')"
          @click.native="onDownload"
        />
        <BaseButton v-else is-small is-outlined has-icon-left @click.native="onDownload">
          <BaseIcon name="download" />
          {{ $t('page.opportunity-forms.download') }}
        </BaseButton>
      </div>
    </div>

    <BaseLoadingBar v-if="!loaded || (form && pendingRendering)" />

    <div v-else-if="hasError" class="form-review__state">
      <LargeNotice
        v-on:click="goBack"
        :title="$t('page.opportunity-forms.form-review.error-title')"
        :subtitle="$t('page.opportunity-forms.form-review.error-subtitle')"
        :buttonText="$t('page.opportunity-forms.form-review.back-to-forms')"
        buttonIcon="arrow-right"
      />
    </div>

    <div v-else class="form-review__content">
      <PDFThumbnailSidebar
        v-if="pdf"
        class="form-review__thumbnails"
        :pdf-document="pdf"
        :current-page="currentPageNumber"
        @page-click="scroll"
      />

      <div class="form-review__pages">
        <PDFDocument show-form-fields @page-rendered="onPageRendered" />
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import LargeNotice from '@/components/notice/LargeNotice.vue';
import PDFDocument from '@/components/pdf-viewer/PDFDocument.vue';
import PDFThumbnailSidebar from '@/components/pdf-viewer/PDFThumbnailSidebar.vue';
import PDFZoom from '@/components/pdf-viewer/PDFZoom.vue';

import { bus } from '@/main';
import { BASE_URL, opportunityDocumentsService } from '@/services/opportunity-documents.service';
import { downloadBlob } from '@/util/download-file';
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageOpportunityFormReview',
  components: {
    BaseRoundButton,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    LargeNotice,
    PDFDocument,
    PDFThumbnailSidebar,
    PDFZoom,
  },
  props: {
    opportunityId: String,
    formId: String,
  },
  data() {
    return {
      loaded: false,
      loadError: false,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('pdfData', ['pdf', 'pendingRendering']),
    ...mapGetters('currentDocument', ['currentPageNumber']),
    ...mapGetters('formFill', ['formFillByDocument']),
    // The form is the opportunity's FORM-type document.
    form() {
      const documents = (this.opportunity && this.opportunity.documents) || [];
      const document = documents.find((d) => d.id === this.formId && d.type === 'FORM');
      if (!document) return null;
      return {
        id: document.id,
        name: document.name,
        pageCount: document.pageCount,
      };
    },
    // True once loading is done but the form failed to load or the id isn't found.
    hasError() {
      return this.loadError || (this.loaded && !this.form);
    },
  },
  async created() {
    try {
      if (!this.opportunity || this.opportunity.id !== this.opportunityId) {
        await this.getOpportunity({ id: this.opportunityId }).catch(() => {});
      }
      await this.getDocumentForms(this.opportunityId);
      if (this.form) {
        // Display the filled output when a successful form-fill record exists;
        // otherwise (never filled or the fill failed) show the original document.
        const fill = this.formFillByDocument(this.formId);
        const pdfUrl =
          fill && fill.status !== 'FAILED'
            ? `${BASE_URL}/${this.opportunityId}/documents/form-fills/${fill.id}/file`
            : `${BASE_URL}/${this.opportunityId}/documents/${this.formId}/file`;
        this.setPdfUrl({ pdfUrl });
      }
    } catch (error) {
      this.loadError = true;
    } finally {
      this.loaded = true;
    }
  },
  beforeDestroy() {
    this.resetPdf();
    this.resetCurrentDocument();
  },
  methods: {
    ...mapActions('opportunity', ['getOpportunity']),
    ...mapActions('formFill', ['getDocumentForms']),
    ...mapActions('pdfData', ['setPdfUrl']),
    ...mapActions('pdfData', { resetPdf: 'RESET' }),
    ...mapActions('currentDocument', ['setCurrentPageNumber']),
    ...mapActions('currentDocument', { resetCurrentDocument: 'RESET' }),
    scroll(page) {
      bus.$emit('scroll-to-page', parseInt(page, 10));
    },
    onPageRendered(pageNumber) {
      this.setCurrentPageNumber(parseInt(pageNumber, 10));
    },
    onDownload() {
      const fill = this.formFillByDocument(this.formId);
      const request =
        fill && fill.status !== 'FAILED'
          ? opportunityDocumentsService.getDocumentFormFile({ opportunityId: this.opportunityId, formFillId: fill.id })
          : opportunityDocumentsService.getDocumentFile({ opportunityId: this.opportunityId, documentId: this.formId });
      request.then((fileBlob) => downloadBlob(fileBlob, this.form.name)).catch((error) => this.$toast.error(error));
    },
    goBack() {
      this.$router.push({ name: 'opportunity-forms', params: { opportunityId: this.opportunityId } });
    },
  },
};
</script>

<style lang="scss">
.form-review {
  display: flex;
  flex-direction: column;
  position: relative;
  height: calc(var(--viewport-height) - #{$menu-top-bar-height});
  background: $color-bg;

  &__topbar {
    display: flex;
    align-items: center;
    gap: $space-md;
    height: $header-height;
    padding-right: 32px;
    background: $color-white;
    border-bottom: 1px solid $color-border;
    flex-shrink: 0;

    // Phones: back, the form's name and download on one 56px line; the zoom over the document.
    @include screen-below('tablet') {
      gap: $space-1;
      height: 56px;
      padding-right: $space-1;
      @include floating-pdf-zoom;
    }
  }

  &__back {
    align-items: center;
    border: none;
    border-right: 1px solid $color-border;
    color: $color-normal;
    cursor: pointer;
    display: flex;
    height: 100%;
    justify-content: center;
    line-height: 1;
    outline: none;
    padding: 16px 32px;
    background: transparent;
    flex-grow: 0;
    min-width: max-content;

    &:hover,
    &:active,
    &:focus {
      color: $color-accent;
    }

    &-icon {
      color: $color-neutral;
      margin-right: 8px;
      width: 18px;
      height: 18px;
    }

    &-text {
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
    }

    @include screen-below('tablet') {
      width: 56px;
      min-width: 0;
      padding: 0;

      &-icon {
        margin-right: 0;
      }

      &-text {
        @include visually-hidden;
      }
    }
  }

  &__name {
    flex: 1;
    display: flex;
    align-items: center;
    gap: $space-sm;
    min-width: 0;
    font-weight: bold;
    color: $color-text-title;
  }

  &__name-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: $color-text-muted;
  }

  &__name-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__zoom {
    flex-shrink: 0;
  }

  &__downloads {
    display: flex;
    gap: $space-sm;
  }

  &__content {
    flex: 1;
    display: flex;
    min-height: 0;
  }

  &__state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $space-3xl $space-xl;
  }

  &__thumbnails {
    flex-shrink: 0;
  }

  &__pages {
    flex: 1;
    overflow-y: auto;
    min-width: 0;
    background: $color-surface-sunken;
  }

  @media (max-width: 1024px) {
    &__content {
      flex-direction: column;
      overflow-y: auto;
    }

    &__thumbnails {
      display: none;
    }
  }
}
</style>
